import { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Collapse, List, ListItem } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';

const NavItem = ({ active, children, icon, info, open: openProp, path, title }) => {
  const [open, setOpen] = useState(openProp);
  const { t } = useTranslation();
  const theme = useTheme();

  const inactiveText = theme.palette.brand?.navItemInactive ?? '#60656B';
  const inactiveIcon = theme.palette.brand?.navItemInactiveIcon ?? '#64686B';
  const activePill = theme.palette.brand?.navItemActivePill ?? '#DDECFB';
  const activeColor = theme.palette.brand?.navItemActiveColor ?? '#345C75';
  const hoverPill = activePill;

  const textColor = active ? activeColor : inactiveText;
  const iconColor = active ? activeColor : inactiveIcon;

  // Branch (has sub-items — kept for expandable groups if used in future)
  if (children) {
    return (
      <ListItem
disableGutters
sx={{ flexDirection: 'column', p: 0 }}
      >
        <ButtonBase
          onClick={() => setOpen(prev => !prev)}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.25,
            height: 40,
            borderRadius: '8px',
            color: inactiveText,
            fontSize: '0.8125rem',
            fontWeight: 500,
            '&:hover': { bgcolor: hoverPill, color: activeColor },
          }}
        >
          {icon && (
            <Box
component="span"
sx={{ display: 'flex', color: inactiveIcon, flexShrink: 0 }}
            >
              {icon}
            </Box>
          )}
          <Box
component="span"
sx={{ flexGrow: 1, textAlign: 'left' }}
          >
            {t(title).toUpperCase()}
          </Box>
          {open ? (
            <ExpandMoreIcon sx={{ fontSize: 16, color: inactiveIcon }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16, color: inactiveIcon }} />
          )}
        </ButtonBase>
        <Collapse
in={open}
sx={{ width: '100%' }}
        >
          <List disablePadding>{children}</List>
        </Collapse>
      </ListItem>
    );
  }

  // Leaf
  return (
    <ListItem
disableGutters
sx={{ p: 0, my: '1px' }}
    >
      <ButtonBase
        component={path ? RouterLink : 'button'}
        to={path}
        end={!!path}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.25,
          height: 40,
          borderRadius: '8px',
          bgcolor: active ? activePill : 'transparent',
          color: textColor,
          fontSize: '0.8125rem',
          fontWeight: active ? 600 : 500,
          textDecoration: 'none',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '&:hover': {
            bgcolor: hoverPill,
            color: activeColor,
            '& .nav-icon': { color: activeColor },
          },
        }}
      >
        {/* Icon */}
        {icon && (
          <Box
            component="span"
            className="nav-icon"
            sx={{ display: 'flex', color: iconColor, flexShrink: 0, fontSize: 20 }}
          >
            {icon}
          </Box>
        )}
        {/* Label */}
        <Box
component="span"
sx={{ flexGrow: 1, textAlign: 'left', lineHeight: 1 }}
        >
          {t(title)}
        </Box>
        {/* Badge / extra info (right-aligned) */}
        {info}
      </ButtonBase>
    </ListItem>
  );
};

NavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  icon: PropTypes.node,
  info: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string,
};

NavItem.defaultProps = {
  active: false,
  open: true,
};

export default NavItem;
